import { StateCreator } from 'zustand'
import { moneyUtils } from '../../utils/money'
import { calculateInvoiceTotals, calculateDueDate } from '../../utils/invoiceCalculations'
import { invoiceApi } from '../../lib/api'
import { companyApi } from '../../lib/api'
import { useAuthStore } from '../authStore'
import { vatOptions } from '../../constants/invoice'
import { debounce } from '../../utils/debounce'
import type { InvoiceStore } from '../invoiceStore'
import type { PaymentStatus, Sender, Recipient, LineItem, Company, CompanyData } from '@dutchinvoice/types'
import type { SyncedInvoice } from '../../types/invoice'
import { useInvoiceStore } from '../invoiceStore'

// Centralized debounced API update functions (500ms delay)
const debouncedInvoiceUpdate = debounce((invoice: SyncedInvoice) => {
    if (invoice.status !== 'concept' && invoice.id && useAuthStore.getState().isAuthenticated) {
        invoiceApi.update(invoice).catch(error => {
            console.error('Failed to update invoice:', error)
        })
    }
}, 500)

const debouncedCompanyUpdate = debounce((companyId: string, companyData: CompanyData & { id: string }) => {
    if (useAuthStore.getState().isAuthenticated) {
        companyApi.update(companyId, companyData).catch(error => {
            console.error('Failed to update company:', error)
        })
    }
}, 500)

const debouncedPersistState = debounce((invoice: SyncedInvoice, setState: Function) => {
    setState((state: InvoiceStore) => {
        const newSavedInvoices = syncWithSavedInvoices(state, invoice)
        localStorage.setItem('dutchinvoice_local_invoices', JSON.stringify(newSavedInvoices))
        return { savedInvoices: newSavedInvoices }
    })
}, 300)

// Helper to sync current invoice with saved invoices
const syncWithSavedInvoices = (state: InvoiceStore, currentInvoice: SyncedInvoice) => {
    // If no invoice number, we can't reliably update savedInvoices
    if (!currentInvoice.invoiceNumber) {
        return state.savedInvoices;
    }

    // First, check if there's an invoice with the same invoice number
    const existingIndex = state.savedInvoices.findIndex(
        inv => inv.invoiceNumber === currentInvoice.invoiceNumber
    );

    // Also check for concept versions with the same ID (for copied invoices)
    const conceptIndex = state.savedInvoices.findIndex(
        inv => inv.id === currentInvoice.id && inv.status === 'concept'
    );

    // Create a new array to avoid mutating the original
    const newSavedInvoices = [...state.savedInvoices];

    // If we found a concept version with the same ID, remove it
    if (conceptIndex >= 0) {
        newSavedInvoices.splice(conceptIndex, 1);
    }

    // If we found an invoice with the same number, update it
    if (existingIndex >= 0) {
        newSavedInvoices[existingIndex] = currentInvoice;
        return newSavedInvoices;
    }

    // If we didn't find an invoice with the same number but removed a concept version,
    // add the current invoice to the array
    if (conceptIndex >= 0 && existingIndex === -1) {
        newSavedInvoices.push(currentInvoice);
        return newSavedInvoices;
    }

    // If we didn't find any matching invoice, return the original array
    return state.savedInvoices;
}

export interface InvoiceEditSlice {
    editingSection: {
        type: 'sender' | 'recipient' | 'item' | 'notes' | 'paymentTerms' | 'status' | 'defaultCompany' | 'discount' | 'invoiceNumber' | 'emailMessage' | 'paymentMethods' | 'schedule' | 'history'
        itemId?: string
    } | null

    updateSender: (field: keyof Sender, value: string) => void
    updateRecipient: (field: keyof Recipient, value: string) => void
    setCompanyFromDatabase: (company: Company) => void
    setCompanyId: (companyId: string | undefined) => void
    addLineItem: () => void
    updateLineItem: (id: string, field: keyof Omit<LineItem, 'id'>, value: string) => void
    removeLineItem: (id: string) => void
    updateNotes: (notes: string, saveAsDefault?: boolean) => void
    updatePaymentTerms: (terms: string) => void
    updateDate: (date: string) => void
    updateEmailMessage: (message: string) => void
    setEditingSection: (section: InvoiceEditSlice['editingSection']) => void
    updatePaymentStatus: (invoiceNumber: string, status: PaymentStatus) => void
    updateReminderSent: (invoiceNumber: string, timestamp: number) => void
    updateDiscount: (discount: { type: 'none' | 'percentage' | 'fixed', value: string, isPlaceholder: boolean }) => void
    duplicateLineItem: (id: string) => void
    updateInvoice: (invoice: Partial<SyncedInvoice>) => void
}

export const createInvoiceEditSlice: StateCreator<
    InvoiceStore,
    [],
    [],
    InvoiceEditSlice
> = (set) => ({
    editingSection: null,

    updateSender: (field, value) =>
        set((state) => {
            const newCurrentInvoice = {
                ...state.currentInvoice,
                sender: {
                    ...state.currentInvoice.sender,
                    [field]: value,
                    isPlaceholder: {
                        ...state.currentInvoice.sender.isPlaceholder,
                        [field]: false
                    }
                }
            }

            // Debounced expensive operations
            debouncedPersistState(newCurrentInvoice, set)
            debouncedInvoiceUpdate(newCurrentInvoice)

            return { currentInvoice: newCurrentInvoice }
        }),

    updateRecipient: (field, value) =>
        set((state) => {
            const newCurrentInvoice = {
                ...state.currentInvoice,
                recipient: {
                    ...state.currentInvoice.recipient,
                    [field]: value,
                    isPlaceholder: {
                        ...state.currentInvoice.recipient.isPlaceholder,
                        [field]: false
                    }
                }
            }

            // If we have a companyId and the user is authenticated, update the company as well
            if (
                newCurrentInvoice.companyId &&
                (field === 'company' || field === 'address' || field === 'postalCode' || field === 'city' || field === 'contactName' || field === 'email' || field === 'additionalInfo')
            ) {
                // Create company data object with appropriate fields
                const companyData: CompanyData & { id: string } = {
                    id: newCurrentInvoice.companyId,
                    name: newCurrentInvoice.recipient.company,
                    address: newCurrentInvoice.recipient.address,
                    postalCode: newCurrentInvoice.recipient.postalCode,
                    city: newCurrentInvoice.recipient.city,
                    contactName: newCurrentInvoice.recipient.contactName || '',
                    email: newCurrentInvoice.recipient.email || '',
                    additionalInfo: newCurrentInvoice.recipient.additionalInfo || ''
                };

                // Debounced company update instead of immediate call
                debouncedCompanyUpdate(newCurrentInvoice.companyId, companyData)
            }

            // Debounced expensive operations
            debouncedPersistState(newCurrentInvoice, set)
            debouncedInvoiceUpdate(newCurrentInvoice)

            return { currentInvoice: newCurrentInvoice }
        }),

    setCompanyFromDatabase: (company) =>
        set((state) => {
            const newCurrentInvoice = {
                ...state.currentInvoice,
                companyId: company.id,
                recipient: {
                    ...state.currentInvoice.recipient,
                    company: company.name,
                    address: company.address,
                    postalCode: company.postalCode,
                    city: company.city,
                    contactName: company.contactName || '',
                    email: company.email || state.currentInvoice.recipient.email || '',
                    additionalInfo: company.additionalInfo || '',
                    isPlaceholder: {
                        ...state.currentInvoice.recipient.isPlaceholder,
                        company: false,
                        address: false,
                        postalCode: false,
                        city: false,
                        contactName: false,
                        email: company.email ? false : state.currentInvoice.recipient.isPlaceholder?.email,
                        additionalInfo: false
                    }
                }
            }

            // Debounced expensive operations
            debouncedPersistState(newCurrentInvoice, set)
            debouncedInvoiceUpdate(newCurrentInvoice)

            return { currentInvoice: newCurrentInvoice }
        }),

    setCompanyId: (companyId) =>
        set((state) => {
            // If companyId is undefined, we're clearing the company association
            // Keep the company name but clear other fields
            const newRecipient = companyId === undefined
                ? {
                    ...state.currentInvoice.recipient,
                    address: '',
                    postalCode: '',
                    city: '',
                    contactName: '',
                    email: '',
                    additionalInfo: '',
                    isPlaceholder: {
                        ...state.currentInvoice.recipient.isPlaceholder,
                        address: true,
                        postalCode: true,
                        city: true,
                        contactName: true,
                        email: true,
                        additionalInfo: true
                    }
                }
                : state.currentInvoice.recipient;

            const newCurrentInvoice = {
                ...state.currentInvoice,
                companyId,
                recipient: newRecipient
            }

            // Debounced expensive operations
            debouncedPersistState(newCurrentInvoice, set)
            debouncedInvoiceUpdate(newCurrentInvoice)

            return { currentInvoice: newCurrentInvoice }
        }),

    addLineItem: () =>
        set((state) => {
            const newItemId = crypto.randomUUID()
            const countryVat = vatOptions.find(c => c.code === state.settings.country)?.vat ?? 21
            const newItem: LineItem = {
                id: newItemId,
                description: '',
                quantity: '1',
                price: '0,00',
                vatRate: String(countryVat),
                isPlaceholder: {
                    description: true,
                    quantity: false,
                    price: true
                }
            }
            const newItems = [...state.currentInvoice.items, newItem]
            const newCurrentInvoice = {
                ...state.currentInvoice,
                items: newItems
            }
            const totals = calculateInvoiceTotals(newItems, state.currentInvoice.discount)

            // Debounced invoice update instead of immediate call
            debouncedInvoiceUpdate(newCurrentInvoice)

            return {
                currentInvoice: newCurrentInvoice,
                editingSection: { type: 'item', itemId: newItemId },
                ...totals
            }
        }),

    updateLineItem: (id, field, value) =>
        set((state) => {
            const newItems = state.currentInvoice.items.map(item =>
                item.id === id ? {
                    ...item,
                    [field]: (field === 'price' || field === 'quantity' || field === 'vatRate')
                        ? moneyUtils.toUniversal(value)
                        : value,
                    isPlaceholder: {
                        ...item.isPlaceholder,
                        [field]: false
                    }
                } : item
            )
            const newCurrentInvoice = {
                ...state.currentInvoice,
                items: newItems
            }

            // Debounced expensive operations
            debouncedPersistState(newCurrentInvoice, set)
            debouncedInvoiceUpdate(newCurrentInvoice)

            // Immediate calculation for now (could be debounced too)
            const totals = calculateInvoiceTotals(newItems, state.currentInvoice.discount)

            return {
                currentInvoice: newCurrentInvoice,
                ...totals
            }
        }),

    removeLineItem: (id) =>
        set((state) => {
            const newItems = state.currentInvoice.items.filter(item => item.id !== id)
            const newCurrentInvoice = {
                ...state.currentInvoice,
                items: newItems
            }

            // Debounced expensive operations
            debouncedPersistState(newCurrentInvoice, set)
            debouncedInvoiceUpdate(newCurrentInvoice)

            // Immediate calculation
            const totals = calculateInvoiceTotals(newItems, state.currentInvoice.discount)

            return {
                currentInvoice: newCurrentInvoice,
                ...totals
            }
        }),

    updateNotes: (notes, saveAsDefault = false) =>
        set((state) => {
            const newCurrentInvoice = {
                ...state.currentInvoice,
                notes,
                isPlaceholder: {
                    ...state.currentInvoice.isPlaceholder,
                    notes: false
                }
            }

            // If saveAsDefault is true, update the settings
            if (saveAsDefault) {
                const { updateSettings } = useInvoiceStore.getState();
                updateSettings({ defaultNotes: notes });
            }

            // Debounced expensive operations
            debouncedPersistState(newCurrentInvoice, set)

            return { currentInvoice: newCurrentInvoice }
        }),

    updateDate: (date) =>
        set((state) => {
            // Assume input is YYYY-MM-DD from UI
            const newDate = date;
            // Calculate due date based on the YYYY-MM-DD date
            const dueDate = calculateDueDate(newDate, state.currentInvoice.paymentTerms);

            const newCurrentInvoice = {
                ...state.currentInvoice,
                date: newDate,
                dueDate
            }

            // Debounced expensive operations
            debouncedPersistState(newCurrentInvoice, set)
            debouncedInvoiceUpdate(newCurrentInvoice)

            return { currentInvoice: newCurrentInvoice }
        }),

    updatePaymentTerms: (terms) =>
        set((state) => {
            const newCurrentInvoice = {
                ...state.currentInvoice,
                paymentTerms: terms,
                dueDate: calculateDueDate(state.currentInvoice.date, terms)
            }

            // Debounced expensive operations
            debouncedPersistState(newCurrentInvoice, set)
            debouncedInvoiceUpdate(newCurrentInvoice)

            return { currentInvoice: newCurrentInvoice }
        }),

    updateEmailMessage: (message: string) => {
        set((state) => ({
            currentInvoice: {
                ...state.currentInvoice,
                emailMessage: message,
                isPlaceholder: {
                    ...state.currentInvoice.isPlaceholder,
                    emailMessage: false
                }
            }
        }))
    },

    setEditingSection: (section) =>
        set({ editingSection: section }),

    updatePaymentStatus: (invoiceNumber, status) =>
        set((state) => {
            // Find the invoice to get its ID
            const invoice = state.savedInvoices.find(inv => inv.invoiceNumber === invoiceNumber)
            if (!invoice) {
                console.error('Invoice not found:', invoiceNumber)
                return state
            }

            // Set paidAt timestamp when status changes to 'paid'
            const paidAt = status === 'paid' ? Date.now() : invoice.paidAt

            // Update local state immediately
            const newSavedInvoices = state.savedInvoices.map(inv =>
                inv.invoiceNumber === invoiceNumber
                    ? { ...inv, paymentStatus: status, paidAt }
                    : inv
            )

            const newCurrentInvoice = state.currentInvoice.invoiceNumber === invoiceNumber
                ? { ...state.currentInvoice, paymentStatus: status, paidAt }
                : state.currentInvoice

            // Always update localStorage for both anonymous and authenticated users
            localStorage.setItem('dutchinvoice_local_invoices', JSON.stringify(newSavedInvoices))

            // Update in the cloud only if user is logged in (immediate for payment status)
            if (useAuthStore.getState().isAuthenticated) {
                // Dates are already in YYYY-MM-DD format
                const updatedInvoice = {
                    ...invoice,
                    paymentStatus: status,
                    paidAt
                }
                invoiceApi.update(updatedInvoice).catch(error => {
                    console.error('Failed to update invoice payment status:', error)
                })
            }

            return {
                savedInvoices: newSavedInvoices,
                currentInvoice: newCurrentInvoice
            }
        }),

    updateReminderSent: (invoiceNumber, timestamp) =>
        set((state) => {
            // Find the invoice
            const invoice = state.savedInvoices.find(inv => inv.invoiceNumber === invoiceNumber)
            if (!invoice) {
                console.error('Invoice not found:', invoiceNumber)
                return state
            }

            // Update local state immediately with reminder timestamp and status
            const newSavedInvoices = state.savedInvoices.map(inv =>
                inv.invoiceNumber === invoiceNumber
                    ? { ...inv, paymentStatus: 'overdue_reminded' as PaymentStatus, lastReminderSentAt: timestamp }
                    : inv
            )

            const newCurrentInvoice = state.currentInvoice.invoiceNumber === invoiceNumber
                ? { ...state.currentInvoice, paymentStatus: 'overdue_reminded' as PaymentStatus, lastReminderSentAt: timestamp }
                : state.currentInvoice

            // Always update localStorage for both anonymous and authenticated users
            localStorage.setItem('dutchinvoice_local_invoices', JSON.stringify(newSavedInvoices))

            // Note: No API call here - the reminder API endpoint already updated the backend
            // This action just syncs the local state after successful API call from component

            return {
                savedInvoices: newSavedInvoices,
                currentInvoice: newCurrentInvoice
            }
        }),

    updateDiscount: (discount: { type: 'none' | 'percentage' | 'fixed', value: string, isPlaceholder: boolean }) =>
        set((state) => {
            const newDiscount = {
                type: discount.type,
                value: discount.type === 'none' ? '0' : discount.value
            };

            const newCurrentInvoice = {
                ...state.currentInvoice,
                discount: newDiscount,
                isPlaceholder: {
                    ...state.currentInvoice.isPlaceholder,
                    discount: discount.isPlaceholder
                }
            };

            // Update local state
            const newSavedInvoices = syncWithSavedInvoices(state, newCurrentInvoice);

            // Always update localStorage for both anonymous and authenticated users
            localStorage.setItem('dutchinvoice_local_invoices', JSON.stringify(newSavedInvoices));

            // Calculate new totals
            const totals = calculateInvoiceTotals(newCurrentInvoice.items, newDiscount);

            return {
                currentInvoice: newCurrentInvoice,
                savedInvoices: newSavedInvoices,
                ...totals
            };
        }),

    duplicateLineItem: (id: string) => {
        set((state) => {
            const itemToDuplicate = state.currentInvoice.items.find(item => item.id === id)
            if (!itemToDuplicate) return state

            const newItem = {
                ...itemToDuplicate,
                id: crypto.randomUUID(),
            }

            return {
                currentInvoice: {
                    ...state.currentInvoice,
                    items: [...state.currentInvoice.items, newItem],
                },
            }
        })
    },

    updateInvoice: (invoiceData) =>
        set((state) => {
            const newCurrentInvoice = {
                ...state.currentInvoice,
                ...invoiceData
            };

            // Update local state
            const newSavedInvoices = syncWithSavedInvoices(state, newCurrentInvoice);

            // Always update localStorage for both anonymous and authenticated users
            localStorage.setItem('dutchinvoice_local_invoices', JSON.stringify(newSavedInvoices));

            // Debounced invoice update instead of immediate call
            debouncedInvoiceUpdate(newCurrentInvoice);

            return {
                currentInvoice: newCurrentInvoice,
                savedInvoices: newSavedInvoices
            };
        }),
})  